import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useTheme } from 'styled-components';
import { AgreementType } from 'api/types';
import { useDownloadContract } from 'api/agreement';
import { toast } from 'shared/components/Toast/Toast';
import { Text } from 'shared/components/Text/Text';
import { SystemDetailsCardsWrapper, SystemDetailsWrapper } from './SystemDetails.styles';
import { InstallPartnerCard } from './SystemDetailsCards/InstallPartnerCard';
import { AutoPayStatusCard } from './SystemDetailsCards/AutoPayStatusCard';
import { ContractCard } from './SystemDetailsCards/ContractCard';

export interface SystemDetailsProps {
  agreement: AgreementType;
  onOpenAutoPay: () => void;
}

export function SystemDetails({ agreement, onOpenAutoPay }: SystemDetailsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [downloadErrorModal, setDownloadErrorModal] = useState(false);

  const { mutate: download } = useDownloadContract({
    onSuccess: (data) => {
      const { presigned_url: presignedUrl } = data;
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = 'signed_everbright_contract.pdf';
      link.click();
      link.remove();
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('projectQualification.somethingWentWrong'),
        theme,
      });
    },
  });

  const {
    id: agreementId,
    organization,
    product_display_name: contractType,
    contract_signed_date: contractSignedDate,
    contract_term: contractTerm,
  } = agreement;

  const formattedDate = contractSignedDate
    ? format(new Date(contractSignedDate), 'MMMM d, yyyy')
    : undefined;

  const handleDownload = () => {
    if (agreement.contract_resource_id) {
      download(agreementId);
    } else {
      setDownloadErrorModal(true);
    }
  };

  return (
    <SystemDetailsWrapper>
      <Text as="h2">{t('status.accountSummary')}</Text>
      <SystemDetailsCardsWrapper>
        {organization && (
          <InstallPartnerCard
            installer={organization.name}
            installerUrl={organization.website_url}
          />
        )}
        {agreement.ach_elected !== null && (
          <AutoPayStatusCard
            currentMilestone={agreement.current_milestone}
            achElected={agreement.ach_elected}
            achDetails={{
              verified: !!agreement.ach_verified,
              bankName: agreement.ach_bank_name,
              accountLastFourDigits: agreement.ach_account_number_last_four_digits,
            }}
            onClick={onOpenAutoPay}
          />
        )}
        <ContractCard
          contractType={contractType}
          contractSignedDate={formattedDate}
          contractTerm={contractTerm || 0}
          onClick={handleDownload}
          downloadErrorModal={downloadErrorModal}
          setDownloadErrorModal={setDownloadErrorModal}
        />
      </SystemDetailsCardsWrapper>
    </SystemDetailsWrapper>
  );
}

export default {};
